import React, { createContext, useContext, useState, useCallback } from 'react';
import { UserRole, RolePermission, DEFAULT_PERMISSIONS } from '@/types/roles';
import { useToast } from '@/hooks/use-toast';

interface RoleContextType {
  currentRole: UserRole | null;
  permissions: RolePermission[];
  setUserRole: (role: UserRole) => void;
  hasPermission: (permissionId: string) => boolean;
  updatePermission: (permissionId: string, enabled: boolean) => void;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

export const RoleProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentRole, setCurrentRole] = useState<UserRole | null>(null);
  const [permissions, setPermissions] = useState<RolePermission[]>([]);
  const { toast } = useToast();

  const setUserRole = useCallback((role: UserRole) => {
    setCurrentRole(role);
    setPermissions(DEFAULT_PERMISSIONS[role]);
    toast({
      title: "Role Updated",
      description: `User role has been set to ${role}`,
    });
  }, [toast]);

  const hasPermission = useCallback((permissionId: string) => {
    return permissions.some(p => p.id === permissionId && p.enabled) || 
           (currentRole === 'admin' && permissionId !== 'manage_all');
  }, [permissions, currentRole]);

  const updatePermission = useCallback((permissionId: string, enabled: boolean) => {
    setPermissions(prev => 
      prev.map(p => p.id === permissionId ? { ...p, enabled } : p)
    );
  }, []);

  return (
    <RoleContext.Provider value={{
      currentRole,
      permissions,
      setUserRole,
      hasPermission,
      updatePermission,
    }}>
      {children}
    </RoleContext.Provider>
  );
};

export const useRole = () => {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};